<template>
  <section class="vehicles-list">
    <vehicles-list-filters
      class="vehicles-list-filters"
      :scrollable="scrollable"
      :per-page="perPage"
    />

    <ul class="vehicles-list-items">
      <li
        v-for="vehicle in list"
        :key="vehicle.id"
        :class="classes(vehicle)"
      >
        <span class="vehicles-list-item-header">
          <span
            :style="styles(vehicle)"
            class="vehicles-list-image"
          />

          <div class="vehicles-list-resume">
            <typography-heading
              class="vehicles-list-title"
              hierarchy="h3"
              bold
            >
              {{ vehicle.vehicle_model.name }}

              <template v-slot:subtitle>
                {{ vehicle.price_cents | monetize }}
              </template>
            </typography-heading>

            <button
              class="vehicles-list-edit"
              icon=""
              @click="popup(vehicle)"
            >
              <font-awesome-icon icon="ellipsis-v" />
            </button>

            <ul
              v-if="popupOpen === vehicle.id"
              class="vehicles-list-popup"
            >
              <li
                v-if="vehicle.active"
                class="vehicles-list-action"
              >
                <a
                  class="vehicles-list-link"
                  target="_blank"
                  :href="showroom(vehicle)"
                >
                  Abrir no Showroom

                  <font-awesome-icon
                    class="vehicles-list-icon"
                    icon="fa-arrow-up-right-from-square"
                  />
                </a>
              </li>

              <li
                v-if="vehicle.active"
                class="vehicles-list-action"
              >
                <span
                  class="vehicles-list-link"
                  tabindex="0"
                  @click="art(vehicle)"
                  @keydown.enter.exact="art(vehicle)"
                >
                  Gerar arte para Story
                </span>
              </li>

              <li class="vehicles-list-action">
                <router-link
                  class="vehicles-list-link"
                  :to="link(vehicle)"
                >
                  Editar veículo
                </router-link>
              </li>

              <li class="vehicles-list-action">
                <span
                  tabindex="0"
                  class="vehicles-list-link variant--danger"
                  :disabled="!vehicle.active"
                  @click="vehicle.active ? remove(vehicle) : null"
                  @keydown.enter.exact="vehicle.active ? remove(vehicle) : null"
                >
                  {{ vehicle.active ? 'Retirar do estoque' : 'Retirado do estoque' }}
                </span>
              </li>
            </ul>
          </div>

          <p class="vehicles-list-info">
            {{ vehicle.vehicle_model.version }}<br>
            {{ vehicle.manufacture_year }} / {{ vehicle.model_year }}
          </p>
        </span>

        <form-button
          class="vehicles-list-footer"
          :href="simulate(vehicle)"
          :disabled="!vehicle.active"
          size="small"
        >
          Fazer simulação
        </form-button>
      </li>

      <context-state
        class="vehicles-list-state"
        :state="currentState"
        tag="li"
      >
        <template v-slot:loading>
          Carregando veículos...
        </template>

        <template v-slot:notfound>
          <typography-heading>
            Seu estoque ainda está vazio

            <template v-slot:subtitle>
              Use essa área para cadastrar e gerenciar seus veículos seminovos
            </template>
          </typography-heading>
        </template>

        <template v-slot:empty>
          Sem mais veículos
        </template>
      </context-state>
    </ul>

    <modal-base
      v-if="artOpen"
      ref="modal"

      title="Gerar arte"

      @close="art"
    >
      <embed-frame
        class="vehicles-list-frame"
        endpoint="vehicles/banner"
        placeholder="Carregando..."

        topless

        :settings="artSettings"
        :callbacks="artCallbacks"
      />
    </modal-base>

    <modal-base
      v-if="removeOpen"
      title="Retirar de estoque"
      center
      @close="remove"
    >
      <vehicles-list-remove
        :id="removeOpen"
        @success="remove"
      />
    </modal-base>
  </section>
</template>

<script>
  import { mapState } from 'vuex';

  import mobile from '@sdk/helpers/interface/mobile';

  import EmbedFrame from '@sdk/views/Embed/Frame.vue';

  import ContextState from '@cordel/components/Context/State.vue';
  import ModalBase from '@cordel/components/Modal/Base.vue';
  import TypographyHeading from '@cordel/components/Typography/Heading.vue';

  // eslint-disable-next-line import/no-extraneous-dependencies
  import persisted from '@/helpers/modules/auth';

  // eslint-disable-next-line import/no-extraneous-dependencies
  import sort from '@/_CRUDL/helpers/data/sort';

  // eslint-disable-next-line import/no-extraneous-dependencies
  import { INITIAL_VEHICLE_FILTERS as INITIAL } from '@/store/modules/used-vehicles';

  import FormButton from '@cordel/components/Form/Button.vue';

  import VehiclesListActions from './List/Actions.vue';
  import VehiclesListFilters from './List/Filters.vue';
  import VehiclesListRemove from './List/Remove.vue';

  export default {
    name: 'VehiclesList',
    components: {
      EmbedFrame,

      ModalBase,
      ContextState,
      TypographyHeading,

      FormButton,

      VehiclesListFilters,
      VehiclesListRemove,
    },
    mixins: [
      mobile,
    ],
    props: {
      perPage: {
        type: Number,
        default: INITIAL.per_page,
      },
      scrollable: {
        type: Boolean,
        default: true,
      },
    },
    data() {
      return {
        nav: {
          action: VehiclesListActions,
        },

        artOpen: false,
        removeOpen: false,
        popupOpen: false,
      };
    },
    computed: {
      ...mapState([
        'usedVehicles',
      ]),

      filters() {
        return this.usedVehicles.list.filters;
      },
      list() {
        return sort(this.usedVehicles.list.items, this.filters.sort);
      },
      loading() {
        return this.usedVehicles.list.loading;
      },
      currentState() {
        if (this.loading) {
          return 'loading';
        }

        if (this.list.length === 0) {
          return 'notfound';
        }

        if (this.scrollable) {
          return 'empty';
        }

        return null;
      },

      artSettings() {
        return {
          vehicles: {
            banner: {
              id: this.artOpen,
            },
          },
        };
      },
      artCallbacks() {
        return {
          scroll: x => x,
        };
      },
    },
    beforeMount() {
      this.$store.dispatch('usedVehicles/clear', { action: 'list' });
    },
    methods: {
      link(vehicle) {
        return {
          name: 'vehicles-read',
          params: {
            id: vehicle.id,
          },
        };
      },
      classes(vehicle) {
        return {
          'vehicles-list-item': true,
          'state--disabled': !vehicle.active,
        };
      },
      simulate(vehicle) {
        return {
          name: 'simulations-new',
          query: {
            vehicle_id: vehicle.id,
          },
        };
      },
      styles(vehicle) {
        const image = vehicle.pictures.find(i => i.main) || vehicle.pictures[0];

        return {
          backgroundImage: `url(${image ? image.file_url : ''})`,
        };
      },

      showroom(vehicle) {
        return `${persisted.environment.split('/api/v1')[0]}/simulador/loja/${vehicle.store.public_api_identifier}/veiculo-usado/${vehicle.public_api_identifier}`;
      },

      art(vehicle = {}) {
        this.artOpen = vehicle.id;
      },

      remove(vehicle = {}) {
        this.removeOpen = vehicle.id;
      },

      popup(vehicle = {}) {
        this.popupOpen = this.popupOpen === vehicle.id ? null : vehicle.id;
      },
    },
  };
</script>

<style lang="scss">
  @import '@cordel/assets/stylesheets/variables/breakpoints.scss';

  .vehicles-list-filters {
    .cordel-section-item {
      margin-top: var(--cordel-spacing-s);

      &:first-child {
        margin-top: 0;
      }
    }
  }
</style>

<style lang="scss" scoped>
  @import '@cordel/assets/stylesheets/mixins/reset.scss';
  @import '@cordel/assets/stylesheets/variables/breakpoints.scss';

  .vehicles-list-items {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: var(--cordel-spacing-xl);
    list-style: none;
    margin: var(--cordel-spacing-xxblaster) var(--product--pwa--horizontal) 0;

    @media all and (max-width: 80rem) {
      gap: var(--cordel-spacing-s);
      grid-template-columns: repeat(3, 1fr);
      margin: var(--cordel-spacing-giga) var(--product--pwa--horizontal) 0;
    }

    @media all and (max-width: 48rem) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media all and (max-width: 32rem) {
      grid-template-columns: repeat(1, 1fr);
    }
  }

  .vehicles-list-item {
    align-items: stretch;
    background-color: var(--cordel-color-constant-light);
    border: var(--cordel-border-width-thin) solid var(--cordel-color-neutral-light);
    border-radius: var(--cordel-border-radius-decorative);
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: var(--cordel-spacing-xs);
  }

  .vehicles-list-content {
    display: block;
    flex: 1;
  }

  .vehicles-list-footer {
    margin-top: var(--cordel-spacing-xxxs);
    position: relative;

    @media all and (max-width: 32rem) {
      margin-top: var(--cordel-spacing-xs);
    }
  }

  .vehicles-list-state {
    grid-column: 1 / -1;
  }

  .vehicles-list-edit {
    @include cordel-input-reset;

    cursor: pointer;
    height: var(--cordel-spacing-xs);
    line-height: var(--cordel-spacing-xs);
    text-align: center;
    padding: 0 var(--cordel-spacing-xs);
    margin-right: calc(var(--cordel-spacing-xs) * -1);
  }

  .vehicles-list-popup {
    background-color: var(--cordel-color-constant-light);
    border-radius: var(--cordel-border-radius-small);
    top: var(--cordel-spacing-xs);
    box-shadow: var(--cordel-box-shadow-around-1);
    font-size: var(--cordel-font-size-m);
    left: 0;
    list-style: none;
    padding: var(--cordel-spacing-xxxs);
    position: absolute;
    width: 100%;
    z-index: 1;
  }

  .vehicles-list-link {
    align-items: center;
    border-radius: var(--cordel-border-radius-small);
    cursor: pointer;
    display: flex;
    flex-direction: row;
    gap: 1em;
    justify-content: space-between;
    padding: var(--cordel-spacing-nano);
    text-decoration: none;

    &.variant--danger {
      color: var(--cordel-color-alert-danger);
    }

    &[disabled] {
      color: var(--cordel-color-neutral-primary);
      cursor: not-allowed;
    }

    &:focus:not([disabled]) {
      outline: none;
      box-shadow: 0 0 0 var(--cordel-border-width-thin) var(--cordel-color-highlight-primary);
    }
  }

  .vehicles-list-item-header {
    align-items: stretch;
    display: flex;
    flex-direction: column-reverse;
    flex-wrap: wrap;
    gap: var(--cordel-spacing-xxxs);
    justify-content: flex-start;
    position: relative;

    @media all and (max-width: 32rem) {
      display: grid;
      grid-auto-flow: row dense;
      grid-template-columns: 40% 1fr;
      grid-template-rows: 1fr 1fr;
      grid-template-areas: "image title" "image info";
    }
  }

  .vehicles-list-resume {
    align-items: flex-start;
    display: flex;
    flex-direction: row;
    justify-content: center;
    order: 3;

    @media all and (max-width: 32rem) {
      grid-area: title;
    }
  }

  .vehicles-list-title {
    flex: 1;
  }

  .vehicles-list-image {
    aspect-ratio: 7 / 5;
    background-position: center;
    background-size: cover;
    border-radius: var(--cordel-border-radius-small);
    display: block;
    order: 2;
    width: 100%;

    .state--disabled & {
      filter: grayscale(1);
      opacity: 0.5;
    }

    @media all and (max-width: 32rem) {
      grid-area: image;
    }
  }

  .vehicles-list-info {
    color: var(--cordel-color-neutral-dark);
    font-size: var(--cordel-font-size-s);
    order: 1;

    @media all and (max-width: 32rem) {
      grid-area: info;
    }
  }
</style>
